import React from 'react';
import { Box } from '@mui/material';
import {
  signalAvatarTones,
  signalBorders,
  signalColors,
  signalDawLogos,
  signalFonts,
  signalGradients,
  signalRadii,
  signalShadows,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  DawTile,
  InitialsAvatar,
  LogoMark,
  Mono,
  ScreenFrame,
  SignalButton,
  StatusPill,
  Waveform,
} from '@/common/components/signal';
import {
  heroWave,
  marketingColumns,
  plans,
  stemsWave,
  versions,
} from '../fixtures';

/** Nav label → the id of the section it jumps to. */
const NAV: [string, string][] = [
  ['Product', 'lp-product'],
  ['Versions', 'lp-versions'],
  ['Collaboration', 'lp-collab'],
  ['Pricing', 'lp-pricing'],
];

/**
 * The sticky-ish header would otherwise land on top of a jumped-to heading.
 * Applied to every anchor target rather than to a scroll container, so it holds
 * regardless of which element is actually doing the scrolling.
 */
const ANCHOR = { scrollMarginTop: '80px' };

const COLLAB_POINTS: [string, string][] = [
  ['Collaborators', 'push versions and pull the full session.'],
  ['Viewers', 'stream renders and read history — no downloads.'],
  ['Conflicts', 'keep both saves as branches instead of overwriting.'],
];

const HOW_STEPS: [string, string][] = [
  [
    'Pick your session folders',
    'Local drives or external SSDs. Nothing else is read.',
  ],
  [
    'Keep working in your DAW',
    'Every save becomes a version you can name, compare and restore.',
  ],
  [
    'Invite whoever needs it',
    'Collaborators pull the session; clients get a stream-only link.',
  ],
];

/** Only the first question is expanded — the rest state the question alone. */
const FAQ: { q: string; a?: string }[] = [
  {
    q: 'Does it need my DAW installed to open a session?',
    a: 'No. Renders and stems stream in the browser. You only need the DAW to open the project file itself.',
  },
  { q: 'What happens if two of us save at once?' },
  { q: 'Can I keep sessions on an external drive?' },
  { q: 'Is my audio encrypted?' },
  { q: 'Windows support?' },
];

const FOOTER_LINKS = ['Download', 'Changelog', 'Status', 'Privacy', 'Terms'];

/** Mono rubric above a section headline. */
const Eyebrow: React.FC<{ children: React.ReactNode; mb: string }> = ({
  children,
  mb,
}) => (
  <Mono
    size={10}
    weight={600}
    letterSpacing="0.14em"
    color={signalColors.accentBright}
    sx={{ display: 'block', marginBottom: mb }}
  >
    {children}
  </Mono>
);

/** The 42/46px display headline shared by the lower marketing sections. */
const Headline: React.FC<{
  children: React.ReactNode;
  size?: number;
  sx?: object;
}> = ({ children, size = 42, sx }) => (
  <Box
    component="h4"
    sx={{
      margin: 0,
      fontFamily: signalFonts.display,
      fontWeight: 800,
      fontSize: `${size}px`,
      lineHeight: 1,
      letterSpacing: '-0.035em',
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * Horizontal rule that opens each lower section. The upper half of the page is
 * one continuous argument; below the fold the sections are separate claims, and
 * the rule is what marks that change of register.
 */
const sectionTop = (paddingTop: string) => ({
  borderTop: `1px solid ${signalBorders.divider}`,
  paddingTop,
});

const WORKS_WITH: [string, string][] = [
  ['Ableton', 'Ableton'],
  ['LogicPro', 'Logic Pro'],
  ['ProTools', 'Pro Tools'],
  ['FLStudio', 'FL Studio'],
  ['Cubase', 'Cubase'],
  ['Reaper', 'Reaper'],
  ['StudioOne', 'Studio One'],
];

const STATS: [string, string, string?][] = [
  ['VERSIONS', '14'],
  ['COLLABORATORS', '3'],
  ['UPLOAD', '18 MB/s', signalColors.accentBright],
];

const FEATURES: {
  rubric: string;
  color: string;
  title: string;
  body: string;
}[] = [
  {
    rubric: '01 / VERSIONS',
    color: signalColors.accentBright,
    title: 'Every save is a take',
    body: 'Roll back the project file without losing the samples you collected after it. Milestones you can name.',
  },
  {
    rubric: '02 / SYNC',
    color: signalColors.cyan,
    title: 'Built for 40 GB sessions',
    body: "The desktop agent moves only what changed, in parallel, straight to storage — and tells you exactly what it's doing.",
  },
  {
    rubric: '03 / COLLAB',
    color: signalColors.green,
    title: 'Hand off without a WeTransfer',
    body: 'Roles, boards and stream-only links so a mixer hears the render before they pull 40 GB.',
  },
];

/**
 * 10 — Landing page.
 *
 * The only public surface in the set, and the only one that argues rather than
 * reports. The proof card on the right is a real piece of the app — the same
 * waveform, status pill and mono metadata used in the project detail screen —
 * so the marketing claim and the product are literally rendered by the same
 * primitives rather than by a mockup that will drift.
 */
export const Screen10Landing: React.FC = () => (
  <ScreenFrame
    index="10"
    title="Landing page"
    note="PUBLIC · ABOVE THE FOLD + PROOF"
    background={`${signalGradients.heroGlow},${signalSurfaces.canvas}`}
  >
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '26px',
        padding: '20px 40px',
        borderBottom: `1px solid ${signalBorders.hairline}`,
      }}
    >
      <LogoMark size={26} />

      <Box
        component="nav"
        sx={{
          display: 'flex',
          gap: '22px',
          fontSize: '13px',
          marginLeft: '14px',
        }}
      >
        {NAV.map(([label, id]) => (
          <Box
            component="a"
            href={`#${id}`}
            key={id}
            sx={{
              color: signalText.secondary,
              textDecoration: 'none',
              '&:hover': { color: signalText.primary },
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '13px',
            color: signalText.secondary,
            cursor: 'pointer',
            '&:hover': { color: signalText.primary },
          }}
        >
          Sign in
        </Box>
        <SignalButton glow={false}>Get the app</SignalButton>
      </Box>
    </Box>

    <Box
      sx={{
        padding: '66px 40px 0',
        display: 'grid',
        gridTemplateColumns: '1.05fr 0.95fr',
        gap: '40px',
        alignItems: 'center',
      }}
    >
      <Box>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            borderRadius: '999px',
            border: '1px solid rgba(255,106,31,0.35)',
            background: 'rgba(255,106,31,0.09)',
            fontFamily: signalFonts.mono,
            fontWeight: 600,
            fontSize: '10px',
            letterSpacing: '0.12em',
            color: signalColors.accentBright,
            marginBottom: '22px',
          }}
        >
          <Box
            component="span"
            sx={{
              width: 5,
              height: 5,
              borderRadius: '999px',
              background: signalColors.accent,
            }}
          />
          VERSION CONTROL FOR MUSIC SESSIONS
        </Box>

        {/* 13ch caps the headline at three lines, which is what gives it the
            stacked, poster-like shape at 76px. */}
        <Box
          component="h3"
          sx={{
            margin: '0 0 18px',
            fontFamily: signalFonts.display,
            fontWeight: 800,
            fontSize: '76px',
            lineHeight: 0.92,
            letterSpacing: '-0.04em',
            maxWidth: '13ch',
          }}
        >
          Never lose a take again.
        </Box>

        <Box
          component="p"
          sx={{
            margin: '0 0 30px',
            fontSize: '16px',
            lineHeight: 1.6,
            color: signalText.secondary,
            maxWidth: '52ch',
            textWrap: 'pretty',
          }}
        >
          Share, collaborate and securely back up your music projects in the
          cloud. StudioVault versions the whole session — samples, renders, the
          project file — so you can roll back to any take and hand a mix off
          without a WeTransfer.
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            marginBottom: '28px',
          }}
        >
          <SignalButton
            size="xl"
            sx={{
              padding: '13px 22px',
              borderRadius: '11px',
              boxShadow: signalShadows.accentXl,
            }}
          >
            Start free — 500 MB
          </SignalButton>
          <SignalButton
            variant="secondary"
            size="xl"
            sx={{
              padding: '13px 20px',
              borderRadius: '11px',
              background: signalSurfaces.fillHover,
              borderColor: signalBorders.stronger,
            }}
          >
            Download for macOS
          </SignalButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <Mono
            size={10}
            weight={600}
            letterSpacing="0.12em"
            color={signalText.faint}
          >
            WORKS WITH
          </Mono>
          <Box sx={{ display: 'flex', gap: '7px', alignItems: 'center' }}>
            {WORKS_WITH.map(([key, label]) => (
              <DawTile
                key={key}
                src={signalDawLogos[key]}
                label={label}
                size={28}
                radius={8}
                sx={{ opacity: 0.85 }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          border: '1px solid rgba(255,255,255,0.11)',
          borderRadius: '18px',
          background: signalSurfaces.cardGradientGlass,
          backdropFilter: 'blur(24px)',
          padding: '20px',
          boxShadow: signalShadows.modal,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '11px',
            marginBottom: '16px',
          }}
        >
          <DawTile
            src={signalDawLogos.Ableton}
            label="Ableton"
            size={32}
            radius={9}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontWeight: 600, fontSize: '14px' }}>
              Nightdrive — rough master v14
            </Box>
            <Mono size={10} sx={{ display: 'block', marginTop: '3px' }}>
              SYNCED 4 MIN AGO · 1.84 GB
            </Mono>
          </Box>
          <StatusPill status="synced" dot>
            SYNCED
          </StatusPill>
        </Box>

        <Box sx={{ position: 'relative' }}>
          <Waveform bars={heroWave} height={128} />
          <Box
            sx={{
              position: 'absolute',
              left: '58%',
              top: 0,
              bottom: 0,
              width: '2px',
              background: signalText.primary,
              boxShadow: '0 0 12px rgba(255,255,255,0.6)',
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '12px',
          }}
        >
          <Mono size={10} color={signalText.faint}>
            00:00
          </Mono>
          <Mono size={10} color={signalText.primary}>
            02:18.44
          </Mono>
          <Mono size={10} color={signalText.faint}>
            03:58
          </Mono>
        </Box>

        <Box
          sx={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: `1px solid ${signalBorders.subtle}`,
            display: 'flex',
            gap: '10px',
          }}
        >
          {STATS.map(([label, value, color]) => (
            <Box
              key={label}
              sx={{
                flex: 1,
                borderRadius: '11px',
                background: signalSurfaces.fill,
                padding: '11px 12px',
              }}
            >
              <Mono size={10} weight={600}>
                {label}
              </Mono>
              <Box
                sx={{
                  fontFamily: signalFonts.display,
                  fontWeight: 700,
                  fontSize: '19px',
                  marginTop: '4px',
                  color: color ?? signalText.primary,
                }}
              >
                {value}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    <Box
      id="lp-product"
      sx={{
        ...ANCHOR,
        padding: '66px 40px 56px',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
      }}
    >
      {FEATURES.map((feature) => (
        <Box
          key={feature.rubric}
          sx={{
            border: `1px solid ${signalBorders.subtle}`,
            borderRadius: `${signalRadii['2xl']}px`,
            background: signalSurfaces.fillSubtle,
            padding: '24px',
          }}
        >
          <Mono
            size={10}
            weight={600}
            letterSpacing="0.14em"
            color={feature.color}
            sx={{ display: 'block', marginBottom: '14px' }}
          >
            {feature.rubric}
          </Mono>
          <Box
            sx={{
              fontFamily: signalFonts.display,
              fontWeight: 700,
              fontSize: '22px',
              letterSpacing: '-0.02em',
              marginBottom: '8px',
            }}
          >
            {feature.title}
          </Box>
          <Box
            component="p"
            sx={{
              margin: 0,
              fontSize: '13.5px',
              lineHeight: 1.6,
              color: signalText.secondary,
            }}
          >
            {feature.body}
          </Box>
        </Box>
      ))}
    </Box>

    {/* How it works — the claim on the left, the receipt on the right. The
        timeline card is the same `versions` fixture the project detail screen
        renders, so the marketing page can't drift from the product. */}
    <Box id="lp-versions" sx={{ ...ANCHOR, padding: '0 40px 60px' }}>
      <Box
        sx={{
          ...sectionTop('56px'),
          display: 'grid',
          gridTemplateColumns: '0.85fr 1.15fr',
          gap: '44px',
          alignItems: 'center',
        }}
      >
        <Box>
          <Eyebrow mb="16px">HOW IT WORKS</Eyebrow>
          <Headline sx={{ marginBottom: '14px', maxWidth: '18ch' }}>
            Install once. Then just save.
          </Headline>
          <Box
            component="p"
            sx={{
              margin: '0 0 26px',
              fontSize: '14.5px',
              lineHeight: 1.6,
              color: signalText.secondary,
              maxWidth: '46ch',
            }}
          >
            Point the desktop agent at the folders you already use. It watches
            for saves, packs up what changed, and pushes a new version in the
            background.
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {HOW_STEPS.map(([title, body], i) => (
              <Box key={title} sx={{ display: 'flex', gap: '14px' }}>
                <Box
                  component="span"
                  sx={{
                    width: 26,
                    height: 26,
                    flex: 'none',
                    borderRadius: `${signalRadii.sm}px`,
                    background: 'rgba(255,106,31,0.14)',
                    color: signalColors.accentBright,
                    fontFamily: signalFonts.mono,
                    fontWeight: 600,
                    fontSize: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {i + 1}
                </Box>
                <Box>
                  <Box sx={{ fontWeight: 600, fontSize: '14px' }}>{title}</Box>
                  <Box
                    sx={{
                      fontSize: '13px',
                      color: signalText.secondary,
                      marginTop: '4px',
                      lineHeight: 1.55,
                    }}
                  >
                    {body}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            border: `1px solid ${signalBorders.medium}`,
            borderRadius: '18px',
            background: signalSurfaces.fillWeak,
            padding: '24px',
            boxShadow: '0 30px 70px -34px rgba(0,0,0,0.95)',
          }}
        >
          <Mono
            size={10}
            weight={600}
            letterSpacing="0.12em"
            sx={{ display: 'block', marginBottom: '18px' }}
          >
            NIGHTDRIVE · HISTORY
          </Mono>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {versions.map((version) => (
              <Box
                key={version.tag}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '26px 1fr',
                  gap: '12px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 11,
                      height: 11,
                      borderRadius: '999px',
                      background: version.dot,
                      boxShadow: `0 0 0 4px ${version.ring}`,
                      marginTop: '4px',
                    }}
                  />
                  {/* The rail continues past the last node — the history is
                      older than what fits, and stopping it flush would imply
                      v10 was the beginning. */}
                  <Box
                    component="span"
                    sx={{
                      flex: 1,
                      width: '1px',
                      background: signalBorders.medium,
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    paddingBottom: '16px',
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '10px',
                  }}
                >
                  <Mono
                    size={12}
                    weight={600}
                    color={version.dot}
                    sx={{ flex: 'none' }}
                  >
                    {version.tag}
                  </Mono>
                  <Box
                    sx={{
                      fontWeight: 600,
                      fontSize: '13.5px',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {version.title}
                  </Box>
                  <Mono size={10} sx={{ flex: 'none' }}>
                    {version.meta}
                  </Mono>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>

    {/* Collaboration — the board at a quarter scale. Reversed against the
        section above it (proof left, claim right) so the two don't read as one
        long column of alternating text. */}
    <Box id="lp-collab" sx={{ ...ANCHOR, padding: '0 40px 60px' }}>
      <Box
        sx={{
          ...sectionTop('56px'),
          display: 'grid',
          gridTemplateColumns: '1.32fr 0.68fr',
          gap: '40px',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            border: `1px solid ${signalBorders.medium}`,
            borderRadius: '18px',
            background: signalSurfaces.fillWeak,
            padding: '22px',
            boxShadow: '0 30px 70px -34px rgba(0,0,0,0.95)',
          }}
        >
          <Mono
            size={10}
            weight={600}
            letterSpacing="0.12em"
            sx={{ display: 'block', marginBottom: '16px' }}
          >
            AUTOCANNON — LP1 · BOARD
          </Mono>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '12px',
              alignItems: 'start',
            }}
          >
            {marketingColumns.map((column) => (
              <Box
                key={column.name}
                sx={{
                  border: `1px solid ${signalBorders.subtle}`,
                  borderRadius: '13px',
                  background: signalSurfaces.fillSubtle,
                  padding: '12px 10px 13px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '9px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    padding: '0 3px',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '999px',
                      background: column.dot,
                    }}
                  />
                  <Mono
                    size={10.5}
                    weight={600}
                    letterSpacing="0.08em"
                    color={signalText.primary}
                  >
                    {column.name}
                  </Mono>
                  <Mono size={10.5} sx={{ marginLeft: 'auto' }}>
                    {column.count}
                  </Mono>
                </Box>

                {column.cards.map((card) => (
                  <Box
                    key={card.name}
                    sx={{
                      border: `1px solid ${card.dragging ? signalBorders.accentHover : signalBorders.default}`,
                      borderRadius: `${signalRadii.md}px`,
                      background: card.dragging
                        ? 'rgba(30,24,20,0.95)'
                        : signalSurfaces.fill,
                      padding: '9px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '7px',
                      boxShadow: card.dragging
                        ? signalShadows.dragging
                        : signalShadows.cardFlat,
                      transform: card.dragging
                        ? 'rotate(-1.6deg) scale(1.02)'
                        : 'none',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', gap: '7px', alignItems: 'center' }}
                    >
                      <DawTile
                        src={card.logo}
                        label={card.daw}
                        size={22}
                        radius={6}
                      />
                      <Box
                        sx={{
                          fontWeight: 600,
                          fontSize: '13px',
                          minWidth: 0,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {card.name}
                      </Box>
                    </Box>

                    <Waveform bars={card.wave} height={16} />

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <StatusPill
                        status={card.tagStatus}
                        borderless
                        size={10}
                        sx={{ padding: '3px 7px' }}
                      >
                        {card.tag}
                      </StatusPill>
                      <InitialsAvatar
                        {...card.avatar}
                        size={19}
                        sx={{ marginLeft: 'auto' }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>

        <Box>
          <Mono
            size={10}
            weight={600}
            letterSpacing="0.14em"
            color={signalColors.green}
            sx={{ display: 'block', marginBottom: '16px' }}
          >
            COLLABORATION
          </Mono>
          {/* Narrower column than the sections above it, so this headline
              steps down a size and gains a little leading to stay on 2 lines. */}
          <Headline
            size={38}
            sx={{ marginBottom: '14px', maxWidth: '16ch', lineHeight: 1.02 }}
          >
            Everyone on the same version.
          </Headline>
          <Box
            component="p"
            sx={{
              margin: '0 0 24px',
              fontSize: '14px',
              lineHeight: 1.6,
              color: signalText.secondary,
            }}
          >
            Boards track a release from writing to master. Roles decide who can
            push, who can download, and who only gets to listen.
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {COLLAB_POINTS.map(([term, rest]) => (
              <Box
                key={term}
                sx={{
                  display: 'flex',
                  gap: '11px',
                  alignItems: 'flex-start',
                  fontSize: '13.5px',
                  color: signalText.secondary,
                  lineHeight: 1.55,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: signalColors.green,
                    fontSize: '11px',
                    paddingTop: '3px',
                  }}
                >
                  ✓
                </Box>
                <Box component="span">
                  <Box component="b" sx={{ color: signalText.primary }}>
                    {term}
                  </Box>{' '}
                  {rest}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>

    <Box id="lp-pricing" sx={{ ...ANCHOR, padding: '0 40px 62px' }}>
      <Box sx={sectionTop('56px')}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: '30px',
            marginBottom: '26px',
          }}
        >
          <Box>
            <Eyebrow mb="14px">PRICING</Eyebrow>
            <Headline>Pay for space, not seats.</Headline>
          </Box>
          <Box
            component="p"
            sx={{
              margin: 0,
              fontSize: '13.5px',
              lineHeight: 1.6,
              color: signalText.secondary,
              maxWidth: '36ch',
            }}
          >
            Collaborators are free on every paid plan. Cancel and your projects
            stay downloadable for 30 days.
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '18px',
          }}
        >
          {plans.map((plan) => (
            <Box
              key={plan.name}
              sx={{
                border: `1px solid ${plan.current ? 'rgba(255,106,31,0.5)' : signalBorders.default}`,
                borderRadius: '18px',
                background: plan.current
                  ? 'rgba(255,106,31,0.07)'
                  : signalSurfaces.fillSubtle,
                padding: '26px',
                display: 'flex',
                flexDirection: 'column',
                gap: '18px',
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '9px',
                    marginBottom: '12px',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '999px',
                      background: plan.accent,
                    }}
                  />
                  <Mono
                    size={11}
                    weight={600}
                    letterSpacing="0.12em"
                    color={signalText.primary}
                  >
                    {plan.name}
                  </Mono>
                </Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}
                >
                  <Headline size={42}>{plan.price}</Headline>
                  <Mono size={12}>{plan.unit}</Mono>
                </Box>
                <Mono
                  size={11}
                  color={plan.accent}
                  sx={{ display: 'block', marginTop: '8px' }}
                >
                  {plan.storage} VAULT
                </Mono>
              </Box>

              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}
              >
                {plan.feats.map((feat) => (
                  <Box
                    key={feat}
                    sx={{
                      display: 'flex',
                      gap: '9px',
                      alignItems: 'flex-start',
                      fontSize: '13px',
                      color: signalText.secondary,
                      lineHeight: 1.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color: plan.accent,
                        fontSize: '11px',
                        paddingTop: '2px',
                      }}
                    >
                      ✓
                    </Box>
                    {feat}
                  </Box>
                ))}
              </Box>

              <SignalButton
                variant={plan.ctaTone === 'accent' ? 'primary' : 'secondary'}
                glow={false}
                sx={{
                  marginTop: 'auto',
                  padding: '11px 18px',
                  borderRadius: `${signalRadii.md}px`,
                  ...(plan.ctaTone === 'muted' && {
                    color: signalText.secondary,
                  }),
                }}
              >
                {plan.cta}
              </SignalButton>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    <Box sx={{ padding: '0 40px 66px' }}>
      <Box
        sx={{
          ...sectionTop('52px'),
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '40px',
        }}
      >
        <Box
          sx={{
            border: `1px solid ${signalBorders.default}`,
            borderRadius: '18px',
            background: signalSurfaces.fillWeak,
            padding: '28px',
          }}
        >
          {/* A waveform as a quotation mark — the testimonial is about audio,
              so the ornament is drawn from the same generator as the players. */}
          <Waveform
            bars={stemsWave}
            height={30}
            barWidth={3}
            gap={3}
            sx={{ marginBottom: '20px', opacity: 0.7 }}
          />
          <Box
            component="p"
            sx={{
              margin: '0 0 20px',
              fontFamily: signalFonts.display,
              fontWeight: 600,
              fontSize: '23px',
              lineHeight: 1.3,
              letterSpacing: '-0.02em',
              textWrap: 'pretty',
            }}
          >
            &ldquo;I stopped keeping thirteen folders called FINAL. The history
            is just there, and my collaborator pulls the session instead of
            waiting on a transfer.&rdquo;
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
            <InitialsAvatar
              initials="MK"
              {...signalAvatarTones.accent}
              size={30}
            />
            <Box>
              <Box sx={{ fontSize: '13px', fontWeight: 600 }}>Mara Kade</Box>
              <Mono size={10.5} sx={{ display: 'block', marginTop: '2px' }}>
                PRODUCER · AUTOCANNON
              </Mono>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Eyebrow mb="18px">QUESTIONS</Eyebrow>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {FAQ.map((entry, i) => (
              <Box
                key={entry.q}
                sx={{
                  padding: '16px 0',
                  borderBottom:
                    i === FAQ.length - 1
                      ? 'none'
                      : `1px solid ${signalBorders.subtle}`,
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                >
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, fontSize: '14px', flex: 1 }}
                  >
                    {entry.q}
                  </Box>
                  <Box component="span" sx={{ color: signalText.muted }}>
                    {entry.a ? '−' : '＋'}
                  </Box>
                </Box>
                {entry.a && (
                  <Box
                    component="p"
                    sx={{
                      margin: '10px 0 0',
                      fontSize: '13px',
                      lineHeight: 1.6,
                      color: signalText.secondary,
                      maxWidth: '56ch',
                    }}
                  >
                    {entry.a}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>

    <Box sx={{ padding: '0 40px 40px' }}>
      <Box
        sx={{
          borderRadius: `${signalRadii['3xl']}px`,
          border: '1px solid rgba(255,106,31,0.32)',
          background: signalSurfaces.fillWeak,
          padding: '54px 40px',
          textAlign: 'center',
        }}
      >
        <Headline size={46} sx={{ marginBottom: '14px' }}>
          Your sessions deserve a vault.
        </Headline>
        <Box
          component="p"
          sx={{
            margin: '0 auto 26px',
            fontSize: '14.5px',
            lineHeight: 1.6,
            color: signalText.secondary,
            maxWidth: '48ch',
          }}
        >
          Start free with 500 MB. Install the agent, save once, and see the
          first version appear.
        </Box>
        <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <SignalButton
            size="xl"
            sx={{
              padding: '13px 24px',
              borderRadius: '11px',
              boxShadow: signalShadows.accentXl,
            }}
          >
            Create free account
          </SignalButton>
          <SignalButton
            variant="secondary"
            size="xl"
            sx={{
              padding: '13px 22px',
              borderRadius: '11px',
              background: signalSurfaces.fillHover,
              borderColor: signalBorders.stronger,
            }}
          >
            Book a walkthrough
          </SignalButton>
        </Box>
      </Box>
    </Box>

    <Box
      component="footer"
      sx={{
        padding: '32px 40px 34px',
        borderTop: `1px solid ${signalBorders.divider}`,
        display: 'flex',
        alignItems: 'center',
        gap: '30px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
        <LogoMark size={22} showWordmark={false} />
        <Mono size={11}>© 2026 STUDIOVAULT</Mono>
      </Box>
      <Box
        sx={{
          marginLeft: 'auto',
          display: 'flex',
          gap: '22px',
          fontSize: '12.5px',
          color: signalText.muted,
        }}
      >
        {FOOTER_LINKS.map((link) => (
          <Box
            component="span"
            key={link}
            sx={{ cursor: 'pointer', '&:hover': { color: signalText.primary } }}
          >
            {link}
          </Box>
        ))}
      </Box>
    </Box>
  </ScreenFrame>
);

export default Screen10Landing;
